/**
 * サードパーティ injected スクリプト由来エラーの判定ユーティリティ。
 *
 * 背景 (#218):
 * `capture_exceptions: true` の posthog-js はグローバル `window.onerror` /
 * `unhandledrejection` を差し込むため、ブラウザ拡張や in-app ブラウザ
 * (LINE / SNS アプリ等) が注入したスクリプトの例外まで、閲覧中のページ
 * (`/home` 等) の `source: "unhandled"` として捕捉してしまう。
 * これらは自アプリのバンドル外で発生しており React ツリーには影響しないが、
 * 同一フィンガープリントで大量のアラートを起票してしまう。
 *
 * `transient.ts` は `console.error` 経由の捕捉を `warn` へ格下げする仕組みで、
 * `source: "unhandled"` の経路は通らない。そのため PostHog の `before_send` で
 * 送信直前に落とす必要がある。
 *
 * 誤って本物のバグを握り潰さないよう、「自アプリのバンドルからは発生し得ない」
 * (未定義グローバル参照であり TypeScript ビルドが通らない) と確認できた
 * 例外文言のみを列挙する。
 */

// ブラウザごとに ReferenceError の文言が異なるため両方を列挙する。
// WebKit: "Can't find variable: X" / Chromium・Firefox: "X is not defined"
const THIRD_PARTY_MESSAGE_PATTERNS: readonly RegExp[] = [
  // iOS Safari の in-app ブラウザが注入するスクリプト由来 (#218)。
  // `EmptyRanges` は src/ にも依存パッケージにも存在しない未定義グローバル。
  /can't find variable:\s*EmptyRanges/i,
  /\bEmptyRanges is not defined\b/i,
] as const;

/**
 * 例外メッセージがサードパーティ injected スクリプト由来ノイズか判定する。
 */
export function isThirdPartyScriptError(message: string): boolean {
  return THIRD_PARTY_MESSAGE_PATTERNS.some((re) => re.test(message));
}

/**
 * PostHog の `$exception` イベントプロパティから例外メッセージを取り出し、
 * サードパーティ injected スクリプト由来ノイズか判定する。
 * `before_send` から呼び、true の場合は当該イベントを送信しない。
 */
export function isThirdPartyScriptException(properties: unknown): boolean {
  return extractExceptionMessages(properties).some(isThirdPartyScriptError);
}

/**
 * `$exception_list[].value` と `$exception_message` から例外文言を収集する。
 * posthog-js のバージョン差で片方しか無いケースがあるため両方を見る。
 */
function extractExceptionMessages(properties: unknown): string[] {
  if (typeof properties !== "object" || properties === null) return [];
  const props = properties as Record<string, unknown>;
  const messages: string[] = [];

  const list = props.$exception_list;
  if (Array.isArray(list)) {
    for (const item of list) {
      if (typeof item !== "object" || item === null) continue;
      const value = (item as { value?: unknown }).value;
      if (typeof value === "string") messages.push(value);
    }
  }

  const message = props.$exception_message;
  if (typeof message === "string") messages.push(message);

  return messages;
}
